import { useEffect, useState } from 'react';
import { onResolveConfirmTemplate } from './_ConfirmTemplates';
import { CONFIRM_TEMPLATE_EVENT, onReadConfirmTemplate } from '../../AppTemplateResolved';

/*
    🔥 HOST DE CONFIRM

    Va UNA sola vez en App.js, dentro del router.
    Las implementaciones standalone devuelven null y no molestan.

    ⚠️ El CSS ya no se importa acá: va en _AppStylesImported.css,
       junto al de sweetalert. Así el orden de carga es predecible.
*/

export const AppConfirmHost = () => {

    const [useTemplateId, setTemplateId] = useState(onReadConfirmTemplate);

    useEffect(() => {

        const onChange = (event) => setTemplateId(event.detail?.id);

        window.addEventListener(CONFIRM_TEMPLATE_EVENT, onChange);
        return () => window.removeEventListener(CONFIRM_TEMPLATE_EVENT, onChange);

    }, []);

    const TEMPLATE = onResolveConfirmTemplate(useTemplateId);

    const HostComponent = TEMPLATE.Host;

    if (!HostComponent) return null;

    return <HostComponent key={TEMPLATE.id} />;
};


/*
    🔥 API ÚNICA PARA TODA LA APP

    const OK = await confirm.danger('¿Eliminar factura?', 'No se puede deshacer');
    if (!OK) return;
*/

const TONE_DEFAULTS = {
    danger: { confirmText: 'Confirmar', cancelText: 'Cancelar' },
    warning: { confirmText: 'Continuar', cancelText: 'Cancelar' },
    info: { confirmText: 'Aceptar', cancelText: 'Cancelar' },
    success: { confirmText: 'Confirmar', cancelText: 'Cancelar' },
};

const onActive = () => onResolveConfirmTemplate(onReadConfirmTemplate());

const onConfirmTone = (tone) => (title, text, options = {}) => {

    // Compatibilidad: varias pantallas historicamente usaban danger() para archivar.
    // No permitimos que una accion de archivo termine mostrando el boton "Eliminar".
    const isArchiveAction = tone === 'danger'
        && typeof title === 'string'
        && /^\s*¿?archivar\b/i.test(title);

    if (isArchiveAction) {
        return onActive().confirm({
            tone: 'warning', icon: 'archive', title, text,
            confirmText: 'Archivar', cancelText: 'Cancelar',
            ...options,
        });
    }

    return onActive().confirm({ tone, title, text, ...TONE_DEFAULTS[tone], ...options });
};

export const confirm = {

    danger: onConfirmTone('danger'),
    warning: onConfirmTone('warning'),
    info: onConfirmTone('info'),
    success: onConfirmTone('success'),

    show: (tone, title, text, options) => onConfirmTone(tone)(title, text, options),

    /*
        🔥 Eliminación con texto de confirmación
        Equivale a SWAL2_ALERT_HELPER.confirmHardDelete, pero funciona
        también con el propio y con react-bootstrap.
    */
    hardDelete: ({ expectedText, title = '¿Eliminar definitivamente?', text, html,
        confirmText = 'Eliminar definitivamente', cancelText = 'Cancelar', requireLabel }) => {

        if (!expectedText) {
            console.warn('[Confirm.hardDelete] Falta `expectedText`.');
            return Promise.resolve(false);
        }

        return onActive().confirm({
            tone: 'danger', title, text, html,
            confirmText, cancelText,
            requireText: expectedText,
            ...(requireLabel ? { requireLabel } : {}),
        });
    },

    /*
        🔥 Archivar — mismo contrato, ícono e intención distintos
    */
    archive: ({ title = '¿Archivar este registro?', text, html,
        confirmText = 'Sí, archivar', cancelText = 'Cancelar' } = {}) => (

        onActive().confirm({ tone: 'warning', icon: 'archive', title, text, html, confirmText, cancelText })
    ),

    /*
        🔥 Eliminación masiva con código por mail

        ⚠️ SOLO existe en sweetalert2 (es tu helper). Con otra
           implementación activa avisa y devuelve null en vez de
           dejar pasar una eliminación masiva sin verificar.
    */
    bulkHardDelete: (options) => {

        const TEMPLATE = onActive();

        if (!TEMPLATE.bulkConfirm) {
            console.warn(`[Confirm.bulkHardDelete] "${TEMPLATE.label}" no lo soporta. Usá sweetAlert.`);
            return Promise.resolve(null);
        }

        return TEMPLATE.bulkConfirm(options);
    },
};
